import AdjustIcon from "@mui/icons-material/Adjust";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { fetchQuestionnaireStructure, fetchUserAnsweredQuestions } from "../../Operator/OperatorAPI/OperatorGet";
import { postSetOverrideNext } from "../../Operator/OperatorAPI/OperatorPost";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m} דק'` : `${m}:${String(s).padStart(2, "0")} דק'`;
};

const UserTimelineDialog = ({ onClose, user }) => {
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expandedStep, setExpandedStep] = useState(user.test_index);
  const [targetPos, setTargetPos] = useState(null);
  const [error, setError] = useState(null);
  const [answeredQuestionIds, setAnsweredQuestionIds] = useState(new Set());

  useEffect(() => {
    setExpandedStep(user.test_index);
  }, [user.test_index]);

  useEffect(() => {
    const loadStructure = async () => {
      try {
        const [structData, detailedData] = await Promise.all([
          fetchQuestionnaireStructure(user.questionnaire),
          fetchUserAnsweredQuestions(user.id)
        ]);
        setStructure(structData);
        setAnsweredQuestionIds(new Set(detailedData.answeredQuestionIds));
      } catch (err) {
        setError("Failed to load questionnaire data");
      } finally {
        setLoading(false);
      }
    };
    loadStructure();
  }, [user.id, user.questionnaire]);

  const handlePositionClick = (pos) => {
    setTargetPos(pos);
    setConfirmOpen(true);
  };

  const handleConfirmOverride = async () => {
    const result = await postSetOverrideNext(user.id, targetPos);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setConfirmOpen(false);
    onClose(); // Close the main dialog after setting override
  };

  if (loading) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  // Helper to determine test completion status
  const getTestCompletion = (test) => {
    let totalQuestions = 0;
    let answeredQuestions = 0;

    test.chapters.forEach(chapter => {
      chapter.questions.forEach(q => {
        totalQuestions++;
        if (answeredQuestionIds.has(q.questionId)) {
          answeredQuestions++;
        }
      });
    });

    if (totalQuestions === 0) {
      // For chapters without questions, we might want to check if the user passed them.
      // But for the icon, we can just use the index for now.
      const testIdx = structure.tests.indexOf(test);
      if (testIdx < user.test_index) return "full";
      if (testIdx === user.test_index) return "partial";
      return "none";
    }

    if (answeredQuestions === totalQuestions) return "full";
    if (answeredQuestions > 0) return "partial";
    return "none";
  };

  // Helper to determine status of a position
  const getPositionStatus = (testName, chapterName, state, questionIndex = null) => {
    // 1. Check if it's the CURRENT position
    const isCurrentPosition =
      testName === structure.tests[user.test_index]?.testName &&
      chapterName === structure.tests[user.test_index]?.chapters[user.chapter_index]?.chapterName &&
      state === user.questionnaire_state &&
      (state !== "QUESTION" || questionIndex === user.questionnaire_question_index_in_chapter);

    if (isCurrentPosition) return "current";

    // 2. For questions, rely ON THE DATABASE
    if (state === "QUESTION" && questionIndex !== null) {
      const test = structure.tests.find(t => t.testName === testName);
      const chapter = test?.chapters.find(c => c.chapterName === chapterName);
      const questionId = chapter?.questions[questionIndex]?.questionId;

      if (questionId && answeredQuestionIds.has(questionId)) {
        return "completed";
      }
      return "future";
    }

    // 3. For non-question states (REFERENCES, CHAPTER_END), use the user's progress index
    const testIdxInStructure = structure.tests.findIndex(t => t.testName === testName);
    const chapterIdxInStructure = structure.tests[testIdxInStructure]?.chapters.findIndex(c => c.chapterName === chapterName);

    if (testIdxInStructure < user.test_index) return "completed";
    if (testIdxInStructure > user.test_index) return "future";

    if (chapterIdxInStructure < user.chapter_index) return "completed";
    if (chapterIdxInStructure > user.chapter_index) return "future";

    // Same test and chapter - check state order
    const stateOrder = {
      "REFERENCES": 0,
      "QUESTION": 1,
      "CHAPTER_END": 2
    };

    const currentStateOrder = stateOrder[user.questionnaire_state] || 0;
    const targetStateOrder = stateOrder[state];

    if (targetStateOrder < currentStateOrder) return "completed";
    return "future";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "success.main";
      case "current": return "primary.main";
      default: return "text.disabled";
    }
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Timeline: {user.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

          <Stepper orientation="vertical" nonLinear sx={{
            overflowY: 'auto',
            maxHeight: '70vh',
            pr: 3, // Space between content and scrollbar
            '& .MuiStepConnector-root:last-child': {
              display: 'none', // Hide the extra connector at the bottom
            },
            '& .MuiStepContent-root': {
              borderLeft: '2px solid',
              borderColor: 'grey.300',
              ml: '11px', // Align vertical line with icon center
              pl: 3,
              mb: -1, // Reduce space after last chapter frame
            }
          }}>
            {structure?.tests.map((test, tIdx) => {
              const testCompletion = getTestCompletion(test);
              const isCurrent = tIdx === user.test_index;

              let icon;
              if (testCompletion === "full") {
                icon = <CheckCircleIcon color="success" />;
              } else if (testCompletion === "partial") {
                const isPassedOrCurrent = tIdx <= user.test_index;
                icon = <AdjustIcon color={isPassedOrCurrent ? "success" : "disabled"} />;
              } else {
                icon = <PanoramaFishEyeIcon color="disabled" />;
              }

              return (
                <Step key={test.testName} active={expandedStep === tIdx} completed={testCompletion === "full"}>
                  <StepLabel
                    icon={icon}
                    onClick={() => setExpandedStep(tIdx)}
                    sx={{ cursor: "pointer", py: 0.5 }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" color={testCompletion === "none" && !isCurrent ? "text.disabled" : "text.primary"}>
                      {test.testName}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 1, mt: 1 }}>
                      {test.chapters.map((chapter, cIdx) => (
                        <Paper key={chapter.chapterName} variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: user.test_index === tIdx && user.chapter_index === cIdx ? 'primary.light' : 'grey.200' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" color={user.test_index === tIdx && user.chapter_index === cIdx ? 'primary.main' : 'text.primary'}>
                              {chapter.chapterName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {chapter.hasReferencesPage && formatTime(chapter.referencesPageTime) && (
                                <Tooltip title="זמן הפניות">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}>
                                    <AssignmentIcon sx={{ fontSize: '0.85rem' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      {formatTime(chapter.referencesPageTime)}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                              {formatTime(chapter.chapterTime) && (
                                <Tooltip title="זמן שאלות">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}>
                                    <AccessTimeIcon sx={{ fontSize: '0.85rem' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                      {formatTime(chapter.chapterTime)}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          <Grid container spacing={1} alignItems="center">
                            {chapter.hasReferencesPage && (
                              <Grid item>
                                <Tooltip title="References Page">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePositionClick({ state: "REFERENCES", testId: test.testName, chapterId: chapter.chapterName, questionIndex: null })}
                                    color={getStatusStatusColor(getPositionStatus(test.testName, chapter.chapterName, "REFERENCES"))}
                                  >
                                    <AssignmentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                            )}

                            {chapter.questions.map((q, qIdx) => (
                              <Grid item key={q.questionId}>
                                <Tooltip title={`Question ${q.questionIndex + 1}`}>
                                  <Box
                                    onClick={() => handlePositionClick({ state: "QUESTION", testId: test.testName, chapterId: chapter.chapterName, questionIndex: q.questionIndex })}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      fontSize: '0.7rem',
                                      fontWeight: 'bold',
                                      bgcolor: (status => {
                                        if (status === "completed") return 'success.light';
                                        if (status === "current") return 'primary.main';
                                        return 'grey.200';
                                      })(getPositionStatus(test.testName, chapter.chapterName, "QUESTION", q.questionIndex)),
                                      color: getPositionStatus(test.testName, chapter.chapterName, "QUESTION", q.questionIndex) === "current" ? "white" : "text.secondary",
                                      '&:hover': { opacity: 0.8, transform: 'scale(1.1)' },
                                      transition: 'all 0.2s',
                                      border: '1px solid',
                                      borderColor: 'rgba(0,0,0,0.05)'
                                    }}
                                  >
                                    {q.questionIndex + 1}
                                  </Box>
                                </Tooltip>
                              </Grid>
                            ))}

                            <Grid item>
                              <Tooltip title="Chapter End">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePositionClick({ state: "CHAPTER_END", testId: test.testName, chapterId: chapter.chapterName, questionIndex: null })}
                                  color={getStatusStatusColor(getPositionStatus(test.testName, chapter.chapterName, "CHAPTER_END"))}
                                >
                                  <DoneAllIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>

        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Move</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to move <b>{user.name}</b> to:
            <br />
            <b>
              {targetPos?.testId} / {targetPos?.chapterId} / {
                targetPos?.state === "QUESTION"
                  ? `question ${targetPos?.questionIndex + 1}`
                  : targetPos?.state.toLowerCase().replace("_", " ")
              }
            </b>?
          </Typography>
          <Typography variant="caption" color="text.secondary" dir="rtl" sx={{ display: "block", mt: 1 }}>
            הנבחן יועבר מיד ותוצג לו הודעה על כך.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmOverride} variant="contained" color="warning">Move Now</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const getStatusStatusColor = (status) => {
  switch (status) {
    case "completed": return "success";
    case "current": return "primary";
    default: return "default";
  }
};

export default UserTimelineDialog;
